import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import './index.css'

const MAX_BARS = 15
const PRIMARY_COLOR = am5.color(0x2b5784)

const DashboardLogsActionsByUser = ({ chartID, dataSet }) => {
  useLayoutEffect(() => {
    const container = document.getElementById(chartID)
    if (!container) return

    const sorted = (dataSet.byUser || [])
      .filter((item) => item.userCode != null && item.userCode !== '')
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_BARS)

    if (!sorted.length) {
      container.style.cssText =
        'display:flex;align-items:center;justify-content:center;color:#aaa;font-size:13px'
      container.textContent = 'No user activity data'
      return () => {
        container.style.cssText = ''
        container.textContent = ''
      }
    }

    container.style.cssText = ''
    container.textContent = ''

    // Reverse so amCharts (bottom-to-top) renders highest value at top
    const chartData = sorted.map((item) => ({ user: item.userCode, count: item.count })).reverse()

    const root = am5.Root.new(chartID)
    root.setThemes([am5themes_Animated.new(root)])
    root._logo.dispose()

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        paddingTop: 4,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 8
      })
    )

    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'user',
        renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 16 })
      })
    )
    yAxis.get('renderer').labels.template.setAll({
      fontSize: 10,
      maxWidth: 100,
      oversizedBehavior: 'truncate',
      textAlign: 'end'
    })
    yAxis.get('renderer').grid.template.set('forceHidden', true)

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        strictMinMax: true,
        maxPrecision: 0,
        renderer: am5xy.AxisRendererX.new(root, {})
      })
    )
    xAxis.get('renderer').grid.template.set('forceHidden', true)
    xAxis.get('renderer').labels.template.setAll({ fontSize: 9 })

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis,
        yAxis,
        valueXField: 'count',
        categoryYField: 'user',
        tooltip: am5.Tooltip.new(root, { labelText: '{categoryY}: {valueX} actions' })
      })
    )

    series.columns.template.setAll({
      fill: PRIMARY_COLOR,
      stroke: PRIMARY_COLOR,
      cornerRadiusTR: 4,
      cornerRadiusBR: 4,
      height: am5.percent(60)
    })

    series.bullets.push(() =>
      am5.Bullet.new(root, {
        locationX: 1,
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          centerX: am5.p100,
          centerY: am5.p50,
          text: '{valueX}',
          populateText: true,
          fontSize: 10,
          fontWeight: 'bold',
          fill: am5.color(0xffffff),
          paddingRight: 6
        })
      })
    )

    yAxis.data.setAll(chartData)
    series.data.setAll(chartData)

    series.appear(1000)
    chart.appear(1000, 100)

    const ro = new ResizeObserver(() => root.resize())
    ro.observe(container)

    return () => {
      ro.disconnect()
      root.dispose()
    }
  }, [chartID, dataSet])

  return <div id={chartID} className='logs-actions-user-chart' />
}

DashboardLogsActionsByUser.propTypes = {
  chartID: PropTypes.string.isRequired,
  dataSet: PropTypes.object.isRequired
}

export default DashboardLogsActionsByUser
